import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMandateContext } from '@/contexts/mandate-context';
import { useMandates } from '@/hooks/use-transactions';
import { useToast } from '@/hooks/use-toast';

interface MandateSelectorProps {
  className?: string;
}

const getMandateInitials = (name: string) => {
  if (!name) return 'CL';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
};

const getMandateStatusDot = (status?: string) => {
  switch (status) {
    case 'active': return 'bg-green-500';
    case 'paused': return 'bg-gray-400';
    default: return 'bg-yellow-500';
  }
};

export const MandateSelector: React.FC<MandateSelectorProps> = ({ className }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedMandateId, setSelectedMandateId } = useMandateContext();
  const { data: allMandates, isLoading: mandatesLoading } = useMandates();
  
  // Find the currently selected mandate
  const currentMandate = allMandates?.find(m => m.id === selectedMandateId);

  const mandateName = currentMandate?.client_name ?? 'Select Mandate';
  const mandateInitials = getMandateInitials(mandateName);

  const handleMandateSwitch = (mandateId: string) => {
    setSelectedMandateId(mandateId);
    toast({
      title: "Mandate switched",
      description: `Now viewing ${allMandates?.find(m => m.id === mandateId)?.client_name}`,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`flex items-center gap-3 p-2 pr-4 bg-background border border-border rounded-full hover:bg-accent transition-colors ${className}`}>
          <div className="relative">
            <div className="w-8 h-8 bg-brand-accent rounded-full flex items-center justify-center font-bold text-brand-primary text-xs">
              {mandateInitials}
            </div>
            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${getMandateStatusDot(currentMandate?.status)}`} />
          </div>
          <div className="text-left">
            <div className="text-brand-primary text-sm font-space-grotesk font-bold">
              {mandateName}
            </div>
            {currentMandate?.status && (
              <div className="text-xs text-muted-foreground capitalize">
                {currentMandate.status === 'active' ? 'Active' : currentMandate.status}
              </div>
            )}
          </div>
          <i className="fa-solid fa-chevron-down text-muted-foreground text-sm"></i>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="flex items-center gap-2">
          <i className="fa-solid fa-building text-sm"></i>
          Current Mandate
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Current mandate info */}
        {currentMandate && (
          <div className="px-2 py-3 mb-2 bg-accent rounded-md">
            <div className="flex items-start gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-brand-accent rounded-full flex items-center justify-center font-bold text-brand-primary">
                  {mandateInitials}
                </div>
                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${getMandateStatusDot(currentMandate.status)}`} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{currentMandate.client_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{currentMandate.status}</p>
              </div>
            </div>
          </div>
        )}

        {/* Switch mandate */}
        {allMandates && allMandates.length > 1 && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Switch Mandate
            </DropdownMenuLabel>
            <ScrollArea className="max-h-60">
              {allMandates
                .filter(m => m.id !== selectedMandateId)
                .map((m) => (
                  <DropdownMenuItem 
                    key={m.id}
                    onClick={() => handleMandateSwitch(m.id)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <div className="relative">
                        <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center font-bold text-xs">
                          {getMandateInitials(m.client_name)}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-background ${getMandateStatusDot(m.status)}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{m.client_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{m.status}</p>
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))}
            </ScrollArea>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem onClick={() => navigate('/client-portfolio')}>
          <i className="fa-solid fa-plus mr-2 text-sm"></i>
          <span>View All Mandates</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};








